import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useUser } from '../../contexts/UserContext';
import { useToast } from '../../contexts/ToastContext';
import { api } from '../../services/api-client';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyData = any;

export function useCoachDashboard() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { coach } = useUser();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [upcomingSessions, setUpcomingSessions] = useState<AnyData[]>([]);
  const [paymentStatus, setPaymentStatus] = useState<AnyData[]>([]);
  const [paymentDialogContext, setPaymentDialogContext] = useState<AnyData | null>(null);
  const [cycleStatus, setCycleStatus] = useState<AnyData[]>([]);
  const [lastTrainedDetails, setLastTrainedDetails] = useState<AnyData[]>([]);
  const [trainingFrequency, setTrainingFrequency] = useState<AnyData[]>([]);
  const [importantDates, setImportantDates] = useState<AnyData[]>([]);
  const [revenueData, setRevenueData] = useState<AnyData[]>([]);
  const [businessAlerts, setBusinessAlerts] = useState<AnyData[]>([]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.workout.fetchUpcomingSessions(),
        api.subscription.fetchClientsPaymentStatus(),
        api.workout.fetchHowLongToFinishCycle(),
        api.workout.fetchLastTrainedDetails(),
        api.workout.fetchTrainingFrequency(),
        api.user.fetchUpcomingImportantDates(),
        api.subscription.fetchRevenueByMonth(),
        api.workout.fetchBusinessAlerts()
      ]);

      const getData = (result: PromiseSettledResult<AnyData>) =>
        result.status === 'fulfilled' ? (result.value?.data ?? []) : [];

      setUpcomingSessions(getData(results[0]));
      setPaymentStatus(getData(results[1]));
      setCycleStatus(getData(results[2]));
      setLastTrainedDetails(getData(results[3]));
      setTrainingFrequency(getData(results[4]));
      setImportantDates(getData(results[5]));
      setRevenueData(getData(results[6]));
      setBusinessAlerts(getData(results[7]));
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      showToast('error', intl.formatMessage({ id: 'error' }), (error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [intl, showToast]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const coachName = coach?.name ?? '';

  const formattedDate = new Date().toLocaleDateString(intl.locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const viewClientProfile = (clientId: number) => {
    // App.tsx registra la ruta como /client-dashboard/:id (sin prefijo /coach).
    // El prefijo provocaba catch-all → / y el coach quedaba "en la home" sin feedback.
    // Ver qa-audit/2026-05-21/bugs (Bug 05).
    navigate(`/client-dashboard/${clientId}`);
  };

  const goToManageStudents = () => {
    // Ver qa-audit/2026-05-21/bugs (Bug 04 — INBOX).
    navigate('/manage-students');
  };

  const openPaymentDialog = (clientId: number) => {
    const client = paymentStatus.find((c: AnyData) => c.clientId === clientId);
    if (!client) return;
    setPaymentDialogContext({
      studentId: clientId,
      oldSubscription: {
        endDate: client.nextPaymentDate ?? undefined
      },
      oldCoachPlan: {
        name: client.planName,
        price: client.planPrice,
        paymentFrequency: client.paymentFrequency
      }
    });
  };

  const closePaymentDialog = () => {
    setPaymentDialogContext(null);
    fetchDashboardData();
  };

  // Derived stats
  const totalClients = paymentStatus.length;
  const totalPaid = paymentStatus.filter((c: AnyData) => c.isPaid).length;
  const unpaidCount = totalClients - totalPaid;

  return {
    loading,
    coachName,
    formattedDate,
    // Widget data
    upcomingSessions,
    paymentStatus,
    cycleStatus,
    lastTrainedDetails,
    trainingFrequency,
    importantDates,
    revenueData,
    businessAlerts,
    // Derived stats
    totalClients,
    totalPaid,
    unpaidCount,
    // Actions
    viewClientProfile,
    goToManageStudents,
    openPaymentDialog,
    closePaymentDialog,
    paymentDialogContext,
    refreshDashboard: fetchDashboardData,
    navigate,
    intl
  };
}
